import { gql, useQuery } from '@apollo/client';
import { type CalendarDate } from '@internationalized/date';
import * as React from 'react';

import { PaginationDirection, TransactionSortKeys, type TransactionFilters } from '../../../__generated__/globalTypes';
import { cx } from '../../../utils/cx';
import { formatCalendarDate, formatDaysUntil, toCurrency } from '../../../utils/formatting';
import {
  calculateYearlySpend,
  formatRecurringType,
  getAverageAmountLabel,
  getFrequencyLabel,
} from '../../../utils/recurring';
import Button from '../../base/buttons/Button';
import CollapseButton from '../../base/buttons/CollapseButton';
import Table, { TableCard } from '../../base/Table';
import Tooltip, { TooltipTrigger } from '../../base/Tooltip';
import { useDateRangeDisplayText, type CalendarDateRange } from '../../DateRangePicker';
import Entity from '../../entity';
import TransactionSlideout from '../../slideouts/TransactionSlideout';
import { AccountSecondaryElement } from '../elements';
import { EmptyTableState, ErrorTableState, LoadingTableState, type IEmptyTableStateProps } from '../helpers';
import useHasTransactionsForEmptyState from '../hooks/useHasTransactionsForEmptyState';

import type {
  GetRecurringStreamTransactionsQuery,
  GetRecurringStreamTransactionsQueryVariables,
  RecurringStreamFragment,
} from './__generated__/RecurringStreamTable.gql';

const PAGE_SIZE = 10;

const RECURRING_STREAM_FRAGMENT = gql`
  fragment RecurringStream on RecurringTransaction {
    id
    description
    type
    frequency
    nextOccurrence
    amountRange
    rawJson
    merchant {
      id
      ...EntityAvatar
    }
    financialAccount {
      id
      name
      mask
      subtype
      ...EntityAvatar
      bank {
        id
        name
      }
    }
  }
  ${Entity.Avatar.Fragment}
`;

// Minimal transaction fragment for recurring stream table display
const RecurringStreamTransactionFragment = gql`
  fragment RecurringStreamTransaction on Transaction {
    id
    amount
    date
    description
    isPending
    financialAccount {
      id
      name
      mask
      subtype
      ...EntityAvatar
      bank {
        id
        name
      }
    }
  }
  ${Entity.Avatar.Fragment}
`;

const GET_RECURRING_STREAM_TRANSACTIONS = gql`
  query GetRecurringStreamTransactions(
    $pagination: PaginationInput!
    $filters: TransactionFilters!
    $sortKey: TransactionSortKeys!
  ) {
    getTransactionsForUser(pagination: $pagination, filters: $filters, sortKey: $sortKey) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        node {
          ...RecurringStreamTransaction
        }
      }
    }
  }
  ${RecurringStreamTransactionFragment}
`;

type Transaction = NonNullable<
  NonNullable<GetRecurringStreamTransactionsQuery['getTransactionsForUser']>['edges']
>[number]['node'];

export type RecurringStream = RecurringStreamFragment;

interface IBaseRecurringStreamTableProps {
  recurringStream: RecurringStream;
  transactions: Transaction[];
  dateRange?: CalendarDateRange;
  isLoadingInitial?: boolean;
  isLoadingMore?: boolean;
  hasError?: boolean;
  hasNextPage: boolean;
  onLoadMore: () => void;
  emptyStateProps?: IEmptyTableStateProps;
}

export const BaseRecurringStreamTable = ({
  recurringStream,
  transactions,
  dateRange,
  isLoadingInitial = false,
  isLoadingMore = false,
  hasError = false,
  hasNextPage,
  onLoadMore,
  emptyStateProps,
}: IBaseRecurringStreamTableProps) => {
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = React.useState<string | null>(null);
  const [isSlideoutOpen, setIsSlideoutOpen] = React.useState(false);

  const title = recurringStream.description;
  const yearlySpend = calculateYearlySpend(recurringStream.amountRange, recurringStream.frequency);
  const dateRangeTextRaw = useDateRangeDisplayText(dateRange);
  const dateRangeText = dateRange ? dateRangeTextRaw : undefined;

  const trailingContent = (
    <div className="flex items-center gap-3">
      <span className="flex items-center gap-2">
        <span className="text-tertiary text-sm font-medium">{formatRecurringType(recurringStream.type)}</span>
        <span className="text-tertiary text-sm font-medium">•</span>
        <span className="flex items-center gap-1">
          <span className="text-tertiary text-sm font-medium">{getFrequencyLabel(recurringStream.frequency)}</span>
          <span className="text-primary text-sm font-medium">{getAverageAmountLabel(recurringStream.amountRange)}</span>
          <span className="text-tertiary text-sm font-medium">({toCurrency(yearlySpend)}/yr)</span>
        </span>
        <span className="text-tertiary text-sm font-medium">•</span>
        <span className="flex items-center gap-1">
          <span className="text-tertiary text-sm font-medium">Next:</span>
          {recurringStream.nextOccurrence ? (
            <Tooltip title={formatCalendarDate(recurringStream.nextOccurrence, 'medium')}>
              <TooltipTrigger>
                <span className="text-primary text-sm font-medium">
                  {formatDaysUntil(recurringStream.nextOccurrence)}
                </span>
              </TooltipTrigger>
            </Tooltip>
          ) : (
            <span className="text-primary text-sm font-medium">Unknown</span>
          )}
        </span>
      </span>
      <CollapseButton isCollapsed={isCollapsed} onToggle={() => setIsCollapsed(!isCollapsed)} />
    </div>
  );

  return (
    <TableCard.Root className="min-w-180 shrink-0 border-none">
      <TableCard.Header title={title} subtitle={dateRangeText} contentTrailing={trailingContent} />
      {!isCollapsed && (
        <>
          {isLoadingInitial && <LoadingTableState />}
          {!isLoadingInitial && hasError && <ErrorTableState />}
          {!isLoadingInitial && !hasError && transactions.length === 0 && emptyStateProps && (
            <EmptyTableState {...emptyStateProps} />
          )}
          {!isLoadingInitial && !hasError && transactions.length > 0 && (
            <Table aria-label="Recurring stream transactions table" className="w-full">
              <Table.Header>
                <Table.Head label="Date" className="px-5" isRowHeader />
                <Table.Head label="" className="px-2" />
                <Table.Head label="Amount" className="flex justify-end pl-4" />
              </Table.Header>
              <Table.Body items={transactions}>
                {(transaction) => (
                  <Table.Row
                    key={transaction.id}
                    className="h-14 cursor-pointer"
                    onClick={() => {
                      setSelectedTransactionId(transaction.id);
                      setIsSlideoutOpen(true);
                    }}
                  >
                    <Table.Cell className="w-0 p-4">
                      <div className="text-primary text-base font-medium">{formatCalendarDate(transaction.date)}</div>
                    </Table.Cell>
                    <Table.Cell className="w-full max-w-0 px-2 py-5">
                      <div className="max-w-full truncate">
                        <AccountSecondaryElement financialAccount={transaction.financialAccount} showName={true} />
                      </div>
                    </Table.Cell>
                    <Table.Cell className={cx('w-0 pl-4', transaction.isPending && 'pt-2.75 pb-2.75')}>
                      <div className="flex flex-col items-end">
                        <div
                          className={cx(
                            'text-base font-medium',
                            Number(transaction.amount) < 0 ? 'text-success-primary' : 'text-primary'
                          )}
                        >
                          {toCurrency(Math.abs(Number(transaction.amount)), {
                            showSign: Number(transaction.amount) < 0,
                          })}
                        </div>
                        {transaction.isPending && <div className="text-tertiary text-xs italic">Pending</div>}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          )}
          {!isLoadingInitial && !hasError && hasNextPage && (
            <div className="bg-secondary_subtle border-secondary flex items-center justify-center gap-3 border-t px-6 pb-4 pt-3">
              <Button size="md" color="secondary" onClick={onLoadMore} isLoading={isLoadingMore}>
                See more transactions
              </Button>
            </div>
          )}
        </>
      )}
      <TransactionSlideout
        transactionId={selectedTransactionId}
        isOpen={isSlideoutOpen}
        onClose={() => setIsSlideoutOpen(false)}
      />
    </TableCard.Root>
  );
};

interface IRecurringStreamTableProps {
  recurringStream: RecurringStream;
  dateRange?: CalendarDateRange;
  onDateRangeChange?: (range: CalendarDateRange) => void;
}

const RecurringStreamTable = ({ recurringStream, dateRange, onDateRangeChange }: IRecurringStreamTableProps) => {
  const filters = React.useMemo<TransactionFilters>(() => {
    const filter: TransactionFilters = {
      recurringTransactionId: recurringStream.id,
    };

    if (dateRange) {
      filter.startDate = dateRange.start.toString() as unknown as CalendarDate;
      filter.endDate = dateRange.end.toString() as unknown as CalendarDate;
    }

    return filter;
  }, [recurringStream.id, dateRange]);

  const { data, loading, error, fetchMore } = useQuery<
    GetRecurringStreamTransactionsQuery,
    GetRecurringStreamTransactionsQueryVariables
  >(GET_RECURRING_STREAM_TRANSACTIONS, {
    variables: {
      pagination: {
        cursor: undefined,
        limit: PAGE_SIZE,
        direction: PaginationDirection.Forward,
      },
      filters,
      sortKey: TransactionSortKeys.Date,
    },
    notifyOnNetworkStatusChange: true,
  });

  const handleLoadMore = React.useCallback(async () => {
    const pageInfo = data?.getTransactionsForUser?.pageInfo;
    if (pageInfo?.endCursor) {
      await fetchMore({
        variables: {
          pagination: {
            cursor: pageInfo.endCursor,
            limit: PAGE_SIZE,
            direction: PaginationDirection.Forward,
          },
          filters,
        },
      });
    }
  }, [data, fetchMore, filters]);

  const transactions = data?.getTransactionsForUser?.edges?.map((edge) => edge.node) ?? [];
  const hasNextPage = data?.getTransactionsForUser?.pageInfo?.hasNextPage ?? false;
  const isLoadingMore = loading && transactions.length > 0;

  const handleDateRangeChange = React.useCallback(
    (range: CalendarDateRange) => {
      onDateRangeChange?.(range);
    },
    [onDateRangeChange]
  );

  const emptyStateProps = useHasTransactionsForEmptyState({
    filters,
    isLoading: loading,
    hasRows: transactions.length > 0,
    currentDateRange: dateRange,
    onViewTransactions: handleDateRangeChange,
  });

  return (
    <BaseRecurringStreamTable
      recurringStream={recurringStream}
      transactions={transactions}
      dateRange={dateRange}
      isLoadingInitial={loading && transactions.length === 0}
      isLoadingMore={isLoadingMore}
      hasError={Boolean(error)}
      hasNextPage={hasNextPage}
      onLoadMore={handleLoadMore}
      emptyStateProps={emptyStateProps}
    />
  );
};

RecurringStreamTable.Fragment = RECURRING_STREAM_FRAGMENT;

export default RecurringStreamTable;
